import { useCallback, useEffect, useRef } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { playNewMessageSound } from '@/lib/sounds'
import { browserNotificationService } from '@/services/browserNotificationService'
import { useOrganizationStore } from '@/store/organization.store'
import { useThreadUIStore } from '@/store/thread-ui.store'
import { useThreadStore } from '@/store/thread.store'
import { useQueryClient } from '@tanstack/react-query'

import { SocketEvent, Thread, ThreadTypeEnum } from '@scrambl/shared'

import {
  EntityRealtimeOptions,
  useEntityRealtime,
} from '../core/useEntityRealtime'
import { BroadcastEventType } from '../core/useRealtime'

// Add debug mode settings
const isBrowser =
  typeof window !== 'undefined' && typeof localStorage !== 'undefined'
const DEBUG_MODE =
  import.meta.env.DEV &&
  isBrowser &&
  (localStorage.getItem('REALTIME_DEBUG') === 'true' ||
    localStorage.getItem('DEBUG_ALL') === 'true')

// Helper functions for conditional logging
function debugLog(..._args: unknown[]) {
  // Debug logging disabled - enable REALTIME_DEBUG in localStorage
}

export interface ThreadMessageReceivedPayload {
  threadId: string
  threadTitle?: string
  organizationId: string
  actorName: string
  messageSnippet?: string
  isDirectMessage: boolean
  timestamp: number
}

export interface UseThreadListRealtimeOptions extends EntityRealtimeOptions {
  onThreadCreated?: (thread: Thread) => void
  onThreadUpdated?: (thread: Thread) => void
  onThreadDeleted?: (data: { id: string }) => void
  onThreadMessageReceived?: (data: ThreadMessageReceivedPayload) => void
  onThreadMemberAdded?: (thread: Thread) => void
}

/**
 * Hook for real-time thread list updates
 * Uses the entity realtime hook for common functionality and centralized broadcasting
 */
export function useThreadListRealtime(
  organizationId: string | null,
  options: UseThreadListRealtimeOptions = {},
) {
  const { addThread, updateThread, removeThread } = useThreadStore()

  // Get current organization and selected thread for native notification logic
  const { currentOrganization } = useOrganizationStore()
  const selectedThreadId = useThreadUIStore.use.selectedThreadId()
  const queryClient = useQueryClient()

  // Track options to avoid stale closures
  const optionsRef = useRef(options)

  // For debugging - track active instances
  const instanceId = useRef(`${Math.random().toString(36).substring(2, 8)}`)

  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options
  }, [options])

  // Log initialization
  useEffect(() => {
    if (DEBUG_MODE && !options.suppressLogging) {
      debugLog(`[ThreadList:${organizationId?.substring(0, 8)}] Initialized`)
    }

    return () => {
      if (DEBUG_MODE && !options.suppressLogging) {
        debugLog(`[ThreadList:${organizationId?.substring(0, 8)}] Unmounted`)
      }
    }
  }, [organizationId, options.suppressLogging])

  // Use the entity hook with organization type
  const { isConnected, addEventListener, broadcastToTabs } = useEntityRealtime(
    'organization',
    organizationId,
    options,
  )

  const mergeHasUnread = useCallback(
    (existing: Thread | undefined, incoming: Thread): Thread => {
      if (!existing) {
        return incoming
      }

      const merged = { ...existing, ...incoming } as Thread
      const shouldPreserveUnread =
        existing.hasUnread === true && incoming.hasUnread !== true
      merged.hasUnread = shouldPreserveUnread
        ? true
        : (incoming.hasUnread ?? existing.hasUnread)

      if (
        incoming.lastCommentPreview === undefined &&
        existing.lastCommentPreview !== undefined
      ) {
        merged.lastCommentPreview = existing.lastCommentPreview
      }

      return merged
    },
    [],
  )

  const updateThreadCache = useCallback(
    (thread: Thread) => {
      queryClient.setQueriesData(
        { queryKey: queryKeys.threads.all, exact: false },
        (existing: unknown) => {
          if (!existing) return existing

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'pages' in existing
          ) {
            const data = existing as {
              pages: Array<{ data?: Thread[] }>
              pageParams: unknown[]
            }

            const pages = data.pages.map(page => {
              if (!page?.data) return page
              if (!page.data.some(item => item.id === thread.id)) return page
              return {
                ...page,
                data: page.data.map(item =>
                  item.id === thread.id ? mergeHasUnread(item, thread) : item,
                ),
              }
            })

            return { ...data, pages }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'data' in existing
          ) {
            const data = existing as { data?: Thread[] }
            if (!data.data || !data.data.some(item => item.id === thread.id)) {
              return existing
            }
            return {
              ...data,
              data: data.data.map(item =>
                item.id === thread.id ? mergeHasUnread(item, thread) : item,
              ),
            }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'id' in existing
          ) {
            const data = existing as Thread
            return data.id === thread.id
              ? mergeHasUnread(data, thread)
              : existing
          }

          return existing
        },
      )
    },
    [mergeHasUnread, queryClient],
  )

  const markThreadUnreadInCache = useCallback(
    (
      threadId: string,
      preview?: { content: string; authorName: string; createdAt: Date },
    ) => {
      queryClient.setQueriesData(
        { queryKey: queryKeys.threads.all, exact: false },
        (existing: unknown) => {
          if (!existing) return existing

          const markUnread = (thread: Thread) => {
            if (thread.id !== threadId) return thread
            if (thread.hasUnread) return thread
            return {
              ...thread,
              hasUnread: true,
              lastCommentPreview: preview ?? thread.lastCommentPreview,
            }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'pages' in existing
          ) {
            const data = existing as {
              pages: Array<{ data?: Thread[] }>
              pageParams: unknown[]
            }

            const pages = data.pages.map(page => {
              if (!page?.data) return page
              if (!page.data.some(item => item.id === threadId)) return page
              return {
                ...page,
                data: page.data.map(markUnread),
              }
            })

            return { ...data, pages }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'data' in existing
          ) {
            const data = existing as { data?: Thread[] }
            if (!data.data || !data.data.some(item => item.id === threadId)) {
              return existing
            }
            return {
              ...data,
              data: data.data.map(markUnread),
            }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'id' in existing
          ) {
            const data = existing as Thread
            return data.id === threadId ? markUnread(data) : existing
          }

          return existing
        },
      )
    },
    [queryClient],
  )

  const removeThreadFromCache = useCallback(
    (threadId: string) => {
      queryClient.setQueriesData(
        { queryKey: queryKeys.threads.all, exact: false },
        (existing: unknown) => {
          if (!existing) return existing

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'pages' in existing
          ) {
            const data = existing as {
              pages: Array<{ data?: Thread[] }>
              pageParams: unknown[]
            }

            const pages = data.pages.map(page => {
              if (!page?.data) return page
              if (!page.data.some(item => item.id === threadId)) return page
              return {
                ...page,
                data: page.data.filter(item => item.id !== threadId),
              }
            })

            return { ...data, pages }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'data' in existing
          ) {
            const data = existing as { data?: Thread[] }
            if (!data.data || !data.data.some(item => item.id === threadId)) {
              return existing
            }
            return {
              ...data,
              data: data.data.filter(item => item.id !== threadId),
            }
          }

          if (
            typeof existing === 'object' &&
            existing !== null &&
            'id' in existing
          ) {
            const data = existing as Thread
            return data.id === threadId ? undefined : existing
          }

          return existing
        },
      )
    },
    [queryClient],
  )

  // Thread created handler
  const handleThreadCreated = useCallback(
    (thread: Thread & { _source?: string }) => {
      // Skip if not for this organization
      if (thread.organizationId !== organizationId) {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Ignoring thread ${thread.id} - wrong organization (expected ${organizationId}, got ${thread.organizationId})`,
        )
        return
      }

      debugLog(
        `[useThreadListRealtime:${instanceId.current}] Processing thread created: ${thread.id}`,
      )

      // Add to thread store
      addThread(thread)

      // Only broadcast to other tabs if this event came from the server (socket)
      // or was generated by this client, not if it came from another tab's broadcast
      if (thread._source !== 'broadcast') {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Broadcasting thread creation to other tabs: ${thread.id}`,
        )
        broadcastToTabs({
          type: BroadcastEventType.THREAD_CREATED,
          entityType: 'thread',
          entityId: thread.id,
          data: { ...thread, _source: 'broadcast' },
          timestamp: Date.now(),
          messageId: `thread-created-${thread.id}-${Date.now()}`,
          source: 'client',
          tabId: Math.random().toString(36).substring(2, 15),
        })
      } else {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Skipping re-broadcast of thread creation: ${thread.id}`,
        )
      }

      // Call custom handler if provided
      if (optionsRef.current.onThreadCreated) {
        optionsRef.current.onThreadCreated(thread)
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Called onThreadCreated callback for ${thread.id}`,
        )
      }
    },
    [organizationId, addThread, broadcastToTabs],
  )

  // Thread updated handler
  const handleThreadUpdated = useCallback(
    (thread: Thread & { _source?: string }) => {
      // Skip if not for this organization
      if (thread.organizationId !== organizationId) {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Ignoring thread update ${thread.id} - wrong organization`,
        )
        return
      }

      debugLog(
        `[useThreadListRealtime:${instanceId.current}] Processing thread updated: ${thread.id}`,
      )

      // Update in thread store
      const existingThread = useThreadStore
        .getState()
        .threads.find(t => t.id === thread.id)
      const mergedThread = mergeHasUnread(existingThread, thread)
      updateThread(mergedThread)
      updateThreadCache(mergedThread)

      // Only broadcast to other tabs if this event came from the server (socket)
      // or was generated by this client, not if it came from another tab's broadcast
      if (thread._source !== 'broadcast') {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Broadcasting thread update to other tabs: ${thread.id}`,
        )
        broadcastToTabs({
          type: BroadcastEventType.THREAD_UPDATED,
          entityType: 'thread',
          entityId: thread.id,
          data: { ...thread, _source: 'broadcast' },
          timestamp: Date.now(),
          messageId: `thread-updated-${thread.id}-${Date.now()}`,
          source: 'client',
          tabId: Math.random().toString(36).substring(2, 15),
        })
      } else {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Skipping re-broadcast of thread update: ${thread.id}`,
        )
      }

      // Call custom handler if provided
      if (optionsRef.current.onThreadUpdated) {
        optionsRef.current.onThreadUpdated(thread)
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Called onThreadUpdated callback for ${thread.id}`,
        )
      }
    },
    [
      organizationId,
      updateThread,
      updateThreadCache,
      mergeHasUnread,
      broadcastToTabs,
    ],
  )

  // Thread deleted handler
  const handleThreadDeleted = useCallback(
    (data: { id: string; organizationId?: string; _source?: string }) => {
      // Skip if not for this organization
      if (data.organizationId && data.organizationId !== organizationId) {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Ignoring thread deletion ${data.id} - wrong organization`,
        )
        return
      }

      debugLog(
        `[useThreadListRealtime:${instanceId.current}] Processing thread deleted: ${data.id}`,
      )

      // Remove from thread store
      removeThread(data.id)
      removeThreadFromCache(data.id)

      // Only broadcast to other tabs if this event came from the server (socket)
      // or was generated by this client, not if it came from another tab's broadcast
      if (data._source !== 'broadcast') {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Broadcasting thread deletion to other tabs: ${data.id}`,
        )
        broadcastToTabs({
          type: BroadcastEventType.THREAD_DELETED,
          entityType: 'thread',
          entityId: data.id,
          data: { ...data, _source: 'broadcast' },
          timestamp: Date.now(),
          messageId: `thread-deleted-${data.id}-${Date.now()}`,
          source: 'client',
          tabId: Math.random().toString(36).substring(2, 15),
        })
      } else {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Skipping re-broadcast of thread deletion: ${data.id}`,
        )
      }

      // Call custom handler if provided
      if (optionsRef.current.onThreadDeleted) {
        optionsRef.current.onThreadDeleted(data)
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Called onThreadDeleted callback for ${data.id}`,
        )
      }
    },
    [organizationId, removeThread, broadcastToTabs],
  )

  // Thread message received handler - for native notifications
  const handleThreadMessageReceived = useCallback(
    (data: ThreadMessageReceivedPayload) => {
      // Skip if not for this organization
      if (data.organizationId !== organizationId) {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Ignoring thread message - wrong organization`,
        )
        return
      }

      debugLog(
        `[useThreadListRealtime:${instanceId.current}] Processing thread message received: ${data.threadId}`,
      )

      // Skip if user is currently viewing this thread
      if (selectedThreadId === data.threadId) {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Skipping notification - user is viewing this thread`,
        )
        return
      }

      const preview = data.messageSnippet
        ? {
            content: data.messageSnippet,
            authorName: data.actorName,
            createdAt: new Date(data.timestamp || Date.now()),
          }
        : undefined

      const currentThread = useThreadStore
        .getState()
        .threads.find(t => t.id === data.threadId)
      if (currentThread && !currentThread.hasUnread) {
        const updatedThread: Thread = {
          ...currentThread,
          hasUnread: true,
          lastCommentPreview: preview ?? currentThread.lastCommentPreview,
        }
        updateThread(updatedThread)
        updateThreadCache(updatedThread)

        if (!data.isDirectMessage) {
          queryClient.setQueryData<number>(
            queryKeys.threads.unreadCount(ThreadTypeEnum.DISCUSSION),
            oldCount =>
              typeof oldCount === 'number' ? oldCount + 1 : oldCount,
          )
        }
      } else if (!currentThread) {
        markThreadUnreadInCache(data.threadId, preview)
      }

      // Build notification title and body
      const title = data.isDirectMessage
        ? `${data.actorName} sent you a message`
        : `${data.actorName} commented in "${data.threadTitle || 'a thread'}"`

      const body = data.messageSnippet || 'New message'

      // Play sound for new message
      playNewMessageSound()

      // Show native notification
      const threadType = data.isDirectMessage ? 'DIRECT_MESSAGE' : 'DISCUSSION'

      void browserNotificationService.showThreadMessageNotification(
        title,
        body,
        data.threadId,
        currentOrganization?.slug,
        threadType,
      )

      // Invalidate unread count query to update sidebar badge
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.threads.all, 'unread-count'],
      })

      // Call custom handler if provided
      if (optionsRef.current.onThreadMessageReceived) {
        optionsRef.current.onThreadMessageReceived(data)
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Called onThreadMessageReceived callback for ${data.threadId}`,
        )
      }
    },
    [
      organizationId,
      selectedThreadId,
      currentOrganization?.slug,
      queryClient,
      updateThread,
      updateThreadCache,
      markThreadUnreadInCache,
    ],
  )

  // Thread member added handler - when current user is added to a thread
  // This event is sent directly to the user's personal room
  const handleThreadMemberAdded = useCallback(
    (thread: Thread & { _source?: string }) => {
      // Skip if not for this organization
      if (thread.organizationId !== organizationId) {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Ignoring thread member added ${thread.id} - wrong organization (expected ${organizationId}, got ${thread.organizationId})`,
        )
        return
      }

      debugLog(
        `[useThreadListRealtime:${instanceId.current}] Processing thread member added: ${thread.id}`,
      )

      // Add to thread store - this will add the thread to the user's list
      addThread(thread)

      // Invalidate thread list queries to ensure the new thread appears
      void queryClient.invalidateQueries({
        queryKey: queryKeys.threads.all,
      })

      // Only broadcast to other tabs if this event came from the server (socket)
      if (thread._source !== 'broadcast') {
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Broadcasting thread member added to other tabs: ${thread.id}`,
        )
        broadcastToTabs({
          type: BroadcastEventType.THREAD_CREATED, // Treat as thread created for other tabs
          entityType: 'thread',
          entityId: thread.id,
          data: { ...thread, _source: 'broadcast' },
          timestamp: Date.now(),
          messageId: `thread-member-added-${thread.id}-${Date.now()}`,
          source: 'client',
          tabId: Math.random().toString(36).substring(2, 15),
        })
      }

      // Call custom handler if provided
      if (optionsRef.current.onThreadMemberAdded) {
        optionsRef.current.onThreadMemberAdded(thread)
        debugLog(
          `[useThreadListRealtime:${instanceId.current}] Called onThreadMemberAdded callback for ${thread.id}`,
        )
      }
    },
    [organizationId, addThread, broadcastToTabs, queryClient],
  )

  // Register event listeners for organization level events using the centralized listener system
  useEffect(() => {
    if (!organizationId) return

    if (DEBUG_MODE && !optionsRef.current.suppressLogging) {
      debugLog(
        `[ThreadList:${organizationId?.substring(0, 8)}] Setting up listeners`,
      )
    }

    // Set up listeners for both socket events and broadcasts from other tabs
    const cleanupThreadCreated = addEventListener(
      SocketEvent.THREAD_CREATED,
      handleThreadCreated,
    )
    const cleanupThreadUpdated = addEventListener(
      SocketEvent.THREAD_UPDATED,
      handleThreadUpdated,
    )
    const cleanupThreadDeleted = addEventListener(
      SocketEvent.THREAD_DELETED,
      handleThreadDeleted,
    )
    const cleanupThreadMessageReceived = addEventListener(
      SocketEvent.THREAD_MESSAGE_RECEIVED,
      handleThreadMessageReceived as unknown as (
        data: Record<string, unknown>,
      ) => void,
    )
    const cleanupThreadMemberAdded = addEventListener(
      SocketEvent.THREAD_MEMBER_ADDED,
      handleThreadMemberAdded,
    )

    // Also listen for centralized broadcast events from other tabs
    const cleanupBroadcastCreated = addEventListener(
      BroadcastEventType.THREAD_CREATED,
      handleThreadCreated,
    )
    const cleanupBroadcastUpdated = addEventListener(
      BroadcastEventType.THREAD_UPDATED,
      handleThreadUpdated,
    )
    const cleanupBroadcastDeleted = addEventListener(
      BroadcastEventType.THREAD_DELETED,
      handleThreadDeleted,
    )

    return () => {
      // Clean up all listeners
      cleanupThreadCreated()
      cleanupThreadUpdated()
      cleanupThreadDeleted()
      cleanupThreadMessageReceived()
      cleanupThreadMemberAdded()
      cleanupBroadcastCreated()
      cleanupBroadcastUpdated()
      cleanupBroadcastDeleted()

      if (DEBUG_MODE && !optionsRef.current.suppressLogging) {
        debugLog(
          `[ThreadList:${organizationId?.substring(0, 8)}] Cleaned up event listeners`,
        )
      }
    }
  }, [
    organizationId,
    addEventListener,
    handleThreadCreated,
    handleThreadUpdated,
    handleThreadDeleted,
    handleThreadMessageReceived,
    handleThreadMemberAdded,
  ])

  return {
    isConnected,
    debug: {
      enableDebug: () => {
        localStorage.setItem('REALTIME_DEBUG', 'true')
      },
      disableDebug: () => {
        localStorage.removeItem('REALTIME_DEBUG')
      },
      organizationId,
      instanceId: instanceId.current,
      isDebugEnabled: DEBUG_MODE,
    },
  }
}
